import { AdaptiveDpr, PresentationControls } from "@react-three/drei";
import { Canvas } from "@react-three/fiber";
import { ITochiItem } from "@snackpass/snackpass-types";
import { Suspense, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AnimationController } from "src/components/AnimationController";
import TochiChicken from "src/components/TochiChicken";
import TochiEgg from "src/components/TochiEgg";
import TochiItem from "src/components/TochiItem";
import {
  getActiveAnim,
  getAnimClips,
  getAnimsDict,
  setActiveAnim,
} from "src/utils/redux/tochiAnimation";
import { getAssets } from "src/utils/redux/tochiAsset";
import { getChicken } from "src/utils/redux/tochiChicken";
import { ITochiChickenEye } from "src/utils/types";
import { AnimationObjectGroup } from "three";

type Props = {
  version: string;
};

export default function ChickenScreen({ version }: Props) {
  const dispatch = useDispatch();

  const chicken = useSelector(getChicken);
  const assets = useSelector(getAssets);
  const animDict = useSelector(getAnimsDict);
  const activeAnim = useSelector(getActiveAnim);
  const clips = useSelector(getAnimClips);

  const animGroup = useRef(new AnimationObjectGroup());
  const [eye, setEye] = useState<ITochiChickenEye>("Default");
  const [remoteItems, setRemoteItems] = useState<ITochiItem[] | null>(null);
  const [chickenVisible, setChickenVisible] = useState(true);
  const [eggVisible, setEggVisible] = useState(true);

  // Messages are sent from client to interact with chicken
  const handleMessage = (message: any) => {
    try {
      const data = JSON.parse(message.data);
      switch (data?.type) {
        case "TOCHI_ITEMS":
          setRemoteItems(data.items);
          break;
        case "TOCHI_ANIM":
          dispatch(setActiveAnim(data.anim));
          break;
        default:
          break;
      }
    } catch (e) {
      return;
    }
  };

  useEffect(() => {
    window.addEventListener("message", handleMessage);
    document.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
      document.removeEventListener("message", handleMessage);
    };
  }, []);

  const handleClick = () => {
    const allowlist = ["chirp", "excite", "flutter_flap", "idle_interact"];
    dispatch(
      setActiveAnim(allowlist[Math.floor(Math.random() * allowlist.length)])
    );
  };

  const handleEvent = (event: string) => {
    switch (event) {
      case "Default":
      case "Closed":
      case "Up":
      case "Down":
      case "Left":
      case "Right":
      case "Mad":
      case "Squeeze":
      case "Surprise":
      case "Droopy":
      case "Sparkle":
      case "UpClosed":
        setEye(event);
        break;
      case "Body_True":
        setChickenVisible(true);
        break;
      case "Body_False":
        setChickenVisible(false);
        break;
      case "Egg_True":
        setEggVisible(true);
        break;
      case "Egg_False":
        setEggVisible(false);
        break;
      case "Confetti":
        break;
      default:
        console.log(`Unrecognized event ${event}`);
        break;
    }
  };

  const activeItems = remoteItems || chicken?.items || [];
  return (
    <Canvas style={{ height: "100vh" }}>
      <ambientLight />
      <pointLight position={[10, 10, 10]} />
      <PresentationControls
        global
        config={{ mass: 2, tension: 200 }}
        snap={false}
        rotation={[0, -Math.PI / 15, 0]}
        polar={[-Math.PI / 3, Math.PI / 3]}
      >
        <group position={[0, -1, 0]} onClick={handleClick}>
          <Suspense fallback={null}>
            <group scale={80}>
              {chicken && assets ? (
                <>
                  <TochiChicken
                    asset={assets.chicken}
                    chicken={chicken}
                    items={activeItems}
                    animGroup={animGroup.current}
                    eye={eye}
                    hide={!chickenVisible}
                  />
                  <TochiEgg
                    asset={assets.egg}
                    chicken={chicken}
                    animGroup={animGroup.current}
                    key={`${chicken._id}-egg`}
                    visible={eggVisible}
                  />
                </>
              ) : null}
              {activeItems.map((item) => (
                <Suspense key={item._id} fallback={null}>
                  <TochiItem item={item} animGroup={animGroup.current} />
                </Suspense>
              ))}
              {clips.length ? (
                <AnimationController
                  active={activeAnim}
                  anims={animDict}
                  clips={clips}
                  animGroup={animGroup}
                  version={version}
                  handleEvent={handleEvent}
                />
              ) : null}
            </group>
          </Suspense>
        </group>
      </PresentationControls>
      {/* TODO remove this makes it faster */}
      <AdaptiveDpr pixelated />
    </Canvas>
  );
}
